/**
 * Query language — parse / serialize / field catalog (PURE).
 *
 * EXPERIMENTAL (lewis/query-language-spike). A human-legible, composable
 * query primitive for the Explore search box. It sits in the "missing
 * middle" between the sidebar (which can only express an AND-stack of
 * `dim IN (ids)` equality selections) and the ⌘K chat agent (which can
 * express anything, but non-deterministically). The grammar maps onto the
 * analytics planner's `FilterExpr` — OR / NOT / comparisons / substring
 * match — that the sidebar throws away even though the proto supports it.
 *
 * Design stance (see queryLangCompile.js for the proto compile target):
 *   - The query box is a REFINEMENT layer. It produces one FilterExpr that
 *     ANDs on top of the sidebar's scopePath-derived filter; the sidebar's
 *     drill-down/lens cascade is untouched. Final filter = scope AND query.
 *   - This module is PURE: tokenizer + recursive-descent parser + serializer
 *     + the static field catalog. No dashboard/React/network imports, so it
 *     unit-tests in isolation and can be reused off the main thread.
 *
 * Grammar (precedence low→high: OR < implicit-AND < NOT < primary):
 *
 *     query   := or
 *     or      := and ( ("OR" | "|") and )*
 *     and     := not ( ("AND" | "&")? not )*        // juxtaposition = AND
 *     not     := ("NOT" | "-") not | primary
 *     primary := "(" or ")" | clause
 *     clause  := FIELD OP VALUE | TERM            // bare TERM = full-text
 *
 * Clause forms:
 *     field:value          eq         (array field → element match / IN)
 *     field:a,b,c          in
 *     field:*val*          contains   (ILIKE; `*` → `%`)
 *     field!=value         ne
 *     field>n field>=n     gt gte     (numeric / timestamp fields)
 *     field<n field<=n     lt lte
 *     -field:value         NOT (field:value)
 *     "some words"         bare full-text term → text contains
 *
 * The parser never throws: it returns { ast, errors, warnings } so the UI
 * can render partial/erroring input live as the user types.
 */

// Leaf data module (no React/network) — keeps this module unit-testable. Used to
// resolve the agent dim's friendly display names (Copilot, Cursor, …) back to
// provider tags so the steer's labels match the breakdown's.
import { PROVIDER_TAG_TO_IDENTITY } from "./agentIdentity.js";

// ── Field catalog ──────────────────────────────────────────────────
//
// One entry per query-language field. `kind` drives which operators are
// valid and how the value compiles. `step`/`field` are the analytics
// FieldRef (verified against analytics_query.proto). v0 covers PROMPT-direct
// fields (the vast majority of sidebar dims live on the PROMPT row) plus the
// synthetic adapter dims; `cluster`/`identity` need cross-entity joins and
// are recognized-but-DEFERRED (kept in the sidebar scopePath for now).
//
//   kind 'string'    eq/ne/in/contains
//   kind 'enum'      eq/ne/in
//   kind 'int'       eq/ne/in/gt/gte/lt/lte
//   kind 'array'     in (element match) / contains (element ILIKE)   [Array(String)]
//   kind 'text'      contains/eq  — full-text ILIKE; HIGH COST (full row scan)
//   kind 'synthetic' membership predicate built by a dim adapter (eq/in)
//   kind 'deferred'  recognized, not compilable in v0 (join-requiring)
//
// `dim` (when present) is the SINGULAR DIM_INFO key — lets the compiler reuse
// the existing adapter registry for synthetic dims, and lets the sidebar
// bridge map a scope step to/from a query clause.
export const QUERY_FIELDS = {
  agent: {
    aliases: ["agents", "provider"], dim: "agent", step: "p",
    field: "DIMENSION_FIELD_PROMPT_PROVIDER", kind: "string",
    // Provider is stored as a canonical lower-kebab tag (`anthropic`,
    // `openai`, …); accept the friendly identity name people actually type.
    normalize: (v) => AGENT_NAME_TO_TAG[v.toLowerCase()] || v,
    desc: "AI provider tag (anthropic, openai, …)",
  },
  endpoint: {
    // Matched by HOSTNAME via a PROMPT → ENDPOINT join (alias `ep`), filtering
    // ENDPOINT.hostname ILIKE. No client id lookup / `endpoint_id IN` list, so
    // no 1024-value cap; the join is LEFT so it composes under OR. Alias `ep`
    // (not `e` — that's the EVENT root in the trace-list spec).
    aliases: ["endpoints", "host", "device", "machine", "machines"], dim: "endpoint", kind: "endpoint", join: "endpoint_host",
    step: "ep", field: "DIMENSION_FIELD_ENDPOINT_HOSTNAME",
    desc: "Endpoint — by hostname (endpoint:laptop, endpoint:*-prod)",
  },
  endpoint_id: {
    aliases: [], dim: "endpoint", step: "p",
    field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID", kind: "string",
    desc: "Endpoint ID - exact inventory identity",
  },
  session: {
    aliases: ["sessions"], dim: "session", step: "p",
    field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID", kind: "string",
    desc: "Trace / notification id",
  },
  model: {
    aliases: ["models"], dim: "model", step: "p",
    field: "DIMENSION_FIELD_PROMPT_MODEL", kind: "string",
    desc: "Request-body model name (e.g. claude-sonnet-4-6)",
  },
  message_type: {
    aliases: ["type", "msg_type"], step: "p",
    field: "DIMENSION_FIELD_PROMPT_MESSAGE_TYPE", kind: "enum",
    desc: "Prompt message type",
  },
  // NOTE: PROMPT_DECISION is deliberately ABSENT. It is fully deprecated dead
  // signal (legacy TYPICAL/ATYPICAL verdict + NON_PROMPT/ERROR markers) — never
  // expose it as a queryable field.
  file: {
    aliases: ["files", "path", "paths", "resource"], dim: "file", step: "p",
    field: "DIMENSION_FIELD_PROMPT_RESOURCE_PATHS", kind: "array",
    desc: "Resource path the prompt touched",
  },
  tool: {
    aliases: ["tools", "tool_call", "tool_calls"], dim: "tool_call", step: "p",
    field: "DIMENSION_FIELD_PROMPT_TOOL_CALL_NAMES", kind: "array",
    desc: "Tool the prompt invoked",
  },
  tool_result: {
    aliases: ["tool_results", "result"], step: "p",
    field: "DIMENSION_FIELD_PROMPT_TOOL_RESULT_NAMES", kind: "array",
    desc: "Tool whose result came back",
  },
  tool_count: {
    aliases: ["tools_count", "tool_call_count"], step: "p",
    field: "DIMENSION_FIELD_PROMPT_TOOL_CALL_COUNT", kind: "int",
    desc: "Number of tool calls in the prompt",
  },
  result_count: {
    aliases: ["tool_result_count"], step: "p",
    field: "DIMENSION_FIELD_PROMPT_TOOL_RESULT_COUNT", kind: "int",
    desc: "Number of tool results in the prompt",
  },
  text: {
    aliases: ["prompt", "content"], step: "p",
    field: "DIMENSION_FIELD_PROMPT_ENRICHED_PROMPT", kind: "text", cost: "high",
    desc: "Full prompt text (substring) — SLOW over wide windows",
  },
  // ── synthetic (adapter-built membership predicates) ──
  skill: { aliases: ["skills"], dim: "skill", step: "p", kind: "synthetic", desc: "Skill touched" },
  mcp_server: { aliases: ["mcp", "mcp_servers"], dim: "mcp_server", step: "p", kind: "synthetic", desc: "MCP server used" },
  memory: { aliases: ["memories"], dim: "memory", step: "p", kind: "synthetic", desc: "Memory file touched" },
  // ── join-requiring (cross-entity) ──
  // The compiler emits the join hop AND a FilterExpr leaf on the joined
  // alias, so these compose under OR/NOT like any PROMPT-direct field. The
  // join is added LEFT so an OR branch keeps unmatched prompts (see
  // queryLangCompile.js / ensureQueryJoins).
  //   cluster   → PROMPT_HAS_EVENT_CLUSTER → ec.cluster_id (numeric)
  //   identity  → PROMPT_HAS_USER_ASSIGNMENTS → … → du.source_id / du.display_name
  //   group     -> the same join -> du.department
  topic: {
    aliases: ["cluster", "clusters", "topics"], dim: "cluster", kind: "cluster", join: "cluster",
    // Filter on the virtual `cluster_subtree` field, NOT the leaf `cluster_id`:
    // the planner lowers it to a recursive `cluster_id IN (subtree)` membership
    // subquery, so `cluster:42` matches node 42 AND its descendants — exactly
    // like clicking that cluster in the sidebar — instead of the exact leaf
    // only. It's a top-level predicate (OR-able) and has no 1024-row cap (the
    // subtree walk is server-side). A non-numeric value is a LABEL, resolved to
    // id(s) before compile (clusterResolver); numeric is an id directly.
    step: "ec", field: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_SUBTREE", numeric: true,
    desc: "Topic — by id (topic:42) or label (topic:auth); matches the subtree",
  },
  identity: {
    aliases: ["identities", "user", "person", "people"], dim: "identity", kind: "identity", join: "identity",
    step: "du", field: "DIMENSION_FIELD_DIRECTORY_USER_SOURCE_ID",
    // A person search: match the resolved directory user by display name, email,
    // OR UPN, so `identity:@corp.com` finds Copilot / Compliance-API accounts
    // (whose person is resolved by email). A UUID-shaped value is an exact
    // directory source_id instead (the stable id the sidebar scopes on).
    searchFields: [
      "DIMENSION_FIELD_DIRECTORY_USER_DISPLAY_NAME",
      "DIMENSION_FIELD_DIRECTORY_USER_EMAIL",
      "DIMENSION_FIELD_DIRECTORY_USER_UPN",
    ],
    desc: "Directory user — by name, email, or @domain (identity:alice, identity:@corp.com)",
  },
  // The person's department: a value is a whole department name in any case,
  // so a group drilled into never takes in another whose name holds its own
  // (Sales, not Sales Ops); `*` matches within one. NO_DEPARTMENT and
  // NOT_IN_DIRECTORY (below) are the groups of those with none. No analytics
  // dim backs it: `dim` only names the welcome page's groups, which the query
  // bar suggests values from (getDimensionEntries `groups`).
  group: {
    aliases: ["groups", "department", "departments", "team", "teams"], dim: "department", kind: "group", join: "identity",
    step: "du", field: "DIMENSION_FIELD_DIRECTORY_USER_DEPARTMENT",
    desc: "Group: a person's department in the directory (group:Sales, group:*eng*)",
  },
};

// The group field's words for having no group, each compiled to a clause of
// its own (queryLangCompile.js): someone the directory gives no department,
// and work on a machine the directory has no one for.
export const NO_DEPARTMENT = "No department";
export const NOT_IN_DIRECTORY = "Not in the directory";

// Fields safe to offer in the sidebar SCOPE STEER — i.e. the fields you can
// filter ON. The steer folds into the shared buildPromptRootAndFilter, so a
// steer on these carries onto every PROMPT-rooted breakdown the same way a scope
// drill does: PROMPT-direct predicates plus the three join fields
// (endpoint-by-hostname, cluster, identity). Excluded as steer FIELDS:
//   - synthetic (flag/skill/mcp_server/memory) → no `field` ref; a `skill:x`
//     filter has no compiled FilterExpr (the dim is an adapter-built membership
//     predicate), so it can't be steered ON. NOTE: this is unrelated to whether
//     the synthetic *dims'* COUNTS honor a steer — they DO, because the steer is
//     threaded into each adapter's count/listEntries → buildPromptRootAndFilter.
//   - cost:high (text/reasoning) → full-text ILIKE; multiplied across ~12 count
//     queries it blows the query budget.
// Under a CARRYABLE steer every standard dim narrows (incl. skills/mcp/memories/
// flags, and clusters/identities via their joins). "—" appears only for a
// NON-carryable steer (a join outside ensureQueryJoins); unsupported values
// fail closed. `signals` is scope-independent (catalog count).
export const STEER_FIELDS = Object.keys(QUERY_FIELDS).filter((k) => {
  const d = QUERY_FIELDS[k];
  return d.kind !== "synthetic" && d.cost !== "high";
});

// Friendly agent name → canonical provider tag. Small, deliberately
// incomplete; unknown names pass through verbatim.
const AGENT_NAME_TO_TAG = {
  claude: "anthropic", anthropic: "anthropic",
  gpt: "openai", chatgpt: "openai", openai: "openai", codex: "openai",
  gemini: "google", google: "google",
};
// Also accept the friendly display names the agent dim shows (Copilot, Cursor,
// …) so `agent:Copilot` resolves to its provider tag (`microsoft`) — keeping the
// steer's value picker, summary, and the breakdown labels aligned. Derived from
// the same tag→identity table the sidebar renders with; the raw tag still works.
for (const [tag, id] of Object.entries(PROVIDER_TAG_TO_IDENTITY)) {
  AGENT_NAME_TO_TAG[tag] = tag;
  if (id?.name) AGENT_NAME_TO_TAG[id.name.toLowerCase()] = tag;
  if (id?.key) AGENT_NAME_TO_TAG[id.key.toLowerCase()] = tag;
}

// Inverse of the agent field's `normalize`, for PARTIAL terms: the canonical
// provider tags whose typed forms (friendly name, identity key, alias, or the
// tag itself) substring-match `term`. Lets backend-space searches accept the
// labels the dashboard renders (ChatG → openai) — the label→value analogue of
// resolving cluster names to ids. Same table `normalize` reads, so a value the
// search surfaces is always one the compiled filter matches.
export function agentTagsMatching(term) {
  const needle = String(term ?? "").trim().toLowerCase();
  if (!needle) return [];
  const tags = new Set();
  for (const [alias, tag] of Object.entries(AGENT_NAME_TO_TAG)) {
    if (alias.includes(needle)) tags.add(tag);
  }
  return [...tags];
}

// Operators valid per field kind. Used for validation warnings and to drive
// the filter-builder's per-field operator dropdown (FilterBuilder.jsx).
export const OPS_BY_KIND = {
  string: new Set(["eq", "ne", "in", "contains"]),
  enum: new Set(["eq", "ne", "in"]),
  int: new Set(["eq", "ne", "in", "gt", "gte", "lt", "lte"]),
  // `tool:Bash` is an element match and `tool!=Bash` its negation (compiled
  // as IN / NOT IN), so both are valid on an array.
  array: new Set(["eq", "ne", "in", "contains"]),
  text: new Set(["contains", "eq"]),
  synthetic: new Set(["eq", "in"]),
  cluster: new Set(["eq", "ne", "in", "contains"]),   // numeric id OR label match
  endpoint: new Set(["eq", "ne", "in", "contains"]),  // hostname (ILIKE) match
  identity: new Set(["eq", "ne", "in", "contains"]),  // source_id + name contains
  group: new Set(["eq", "ne", "in", "contains"]),     // whole department name (ILIKE)
};

// Build the alias → canonical-key lookup once.
const FIELD_LOOKUP = (() => {
  const m = new Map();
  for (const [key, def] of Object.entries(QUERY_FIELDS)) {
    m.set(key.toLowerCase(), key);
    for (const a of def.aliases || []) m.set(a.toLowerCase(), key);
  }
  return m;
})();

// Resolve a field token (case-insensitive, alias-aware) → { key, def } or null.
export function resolveField(token) {
  const key = FIELD_LOOKUP.get(String(token || "").toLowerCase());
  if (!key) return null;
  return { key, def: QUERY_FIELDS[key] };
}

// Suggest field keys for autocomplete given a partial token (prefix match on
// canonical key OR any alias). Returns canonical keys, deduped, in catalog order.
export function suggestFields(partial, allow = null) {
  const p = String(partial || "").toLowerCase();
  const allowSet = allow ? new Set(allow) : null;
  const out = [];
  for (const [key, def] of Object.entries(QUERY_FIELDS)) {
    if (allowSet && !allowSet.has(key)) continue;
    const hit = key.toLowerCase().startsWith(p)
      || (def.aliases || []).some((a) => a.toLowerCase().startsWith(p));
    if (hit) out.push(key);
  }
  return out;
}

// ── Value autocomplete context (pure) ───────────────────────────────
//
// Only `:` and `!=` carry enumerable values; numeric `>`,`<`,`>=`,`<=` don't.
const VALUE_OP_RE = /^(-?)([A-Za-z_][\w]*)(!=|:)([\s\S]*)$/;

/**
 * Given the full query text and the caret offset, return the value-autocomplete
 * context when the caret sits in the VALUE of a `field:` / `field!=` clause:
 *
 *   { fieldKey, def, term, valueStart }
 *     fieldKey   — canonical QUERY_FIELDS key (alias-resolved)
 *     def        — its catalog entry
 *     term       — the partial under the caret to match on: the segment after
 *                  the last comma in a list, with a leading `*` and surrounding
 *                  quotes stripped (so `cluster:a,*pr` → term `pr`)
 *     valueStart — offset in `text` where that raw segment begins, so the caller
 *                  can replace text[valueStart..caret] with the chosen value
 *
 * Returns null on a bare field token (→ field autocomplete), after a numeric
 * operator, or an unknown field. A quoted value containing spaces stops the
 * left-scan at the space — autocomplete covers the pre-space partial and the
 * picked suggestion re-inserts the fully-quoted form.
 */
export function parseValueContext(text, caret) {
  const s = String(text ?? "");
  const c = Math.max(0, Math.min(caret ?? s.length, s.length));
  let start = c;
  while (start > 0 && !/[\s()]/.test(s[start - 1])) start -= 1;
  const tokenLeft = s.slice(start, c);
  const m = VALUE_OP_RE.exec(tokenLeft);
  if (!m) return null;
  const [, neg, fieldTok, op, rawValue] = m;
  const field = resolveField(fieldTok);
  if (!field) return null;
  const lastComma = rawValue.lastIndexOf(",");
  const segStartInValue = lastComma >= 0 ? lastComma + 1 : 0;
  const rawSeg = rawValue.slice(segStartInValue);
  const valueStart = start + neg.length + fieldTok.length + op.length + segStartInValue;
  const term = rawSeg.replace(/^"/, "").replace(/\*/g, "");
  return { fieldKey: field.key, def: field.def, term, valueStart };
}

/**
 * Wrap a value in double quotes when it contains characters the tokenizer
 * splits on (whitespace, comma, parens, or an operator char) — e.g. a cluster
 * name with spaces. Embedded double-quotes are dropped (vanishingly rare in
 * real labels, and the value parser has no escape form).
 */
export function quoteValueIfNeeded(value) {
  const v = String(value ?? "");
  if (v === "") return v;
  if (/[\s(),:<>!=]/.test(v)) return `"${v.replace(/"/g, "")}"`;
  return v;
}

// Fields whose VALUES the query bar can enumerate for autocomplete (see
// queryLangSuggest.js). `cluster` is served from the local cluster tree; the
// rest map to analytics dim-entries dims that ARE in
// ANALYTICS_SUPPORTED_DIM_PLURALS. `file` is deliberately ABSENT: its dim
// ("files") is NOT analytics-supported, so getDimensionEntries short-circuits to
// empty — value autocomplete there would be a silent "Searching…" → no-op. It
// still works as a FILTER (`file:*.env`), just without suggestions. Likewise
// message_type / tool_result / the numeric + full-text fields have no
// enumerable source. The single source of truth for both the QueryBar's
// value-mode gate and the suggester.
const VALUE_SUGGEST_FIELDS = new Set([
  "topic", "agent", "endpoint", "model", "session", "identity", "group",
  "tool", "skill", "mcp_server", "memory",
]);

export function isValueSuggestField(fieldKey) {
  return VALUE_SUGGEST_FIELDS.has(resolveField(fieldKey)?.key || fieldKey);
}

// ── Tokenizer ───────────────────────────────────────────────────────
//
// Splits raw input into paren tokens and "word" tokens. A word is a maximal
// run of non-space, non-paren characters, EXCEPT a double-quoted span may
// contain spaces and parens (quotes are preserved here; the value parser
// strips them). Tracks each token's source [start,end) offset for the UI.

const OPS = ["!=", ">=", "<=", ":", ">", "<"]; // longest-first match order
const OP_NAME = { ":": null, "!=": "ne", ">": "gt", ">=": "gte", "<": "lt", "<=": "lte" };

function tokenize(input) {
  const tokens = [];
  let i = 0;
  const n = input.length;
  while (i < n) {
    const c = input[i];
    if (c === " " || c === "\t" || c === "\n") { i++; continue; }
    if (c === "(" || c === ")") {
      tokens.push({ type: c === "(" ? "lparen" : "rparen", start: i, end: i + 1 });
      i++;
      continue;
    }
    // Read a word, honoring quoted spans.
    const start = i;
    let buf = "";
    while (i < n) {
      const ch = input[i];
      if (ch === " " || ch === "\t" || ch === "\n" || ch === "(" || ch === ")") break;
      if (ch === '"') {
        // Consume through the closing quote (or EOF).
        buf += ch; i++;
        while (i < n && input[i] !== '"') { buf += input[i]; i++; }
        if (i < n) { buf += input[i]; i++; } // closing quote
        continue;
      }
      buf += ch; i++;
    }
    tokens.push({ type: "word", value: buf, start, end: i });
  }
  return tokens;
}

const KEYWORDS = new Set(["or", "and", "not"]);

// Strip a single layer of surrounding double quotes.
function unquote(s) {
  if (s.length >= 2 && s[0] === '"' && s[s.length - 1] === '"') return s.slice(1, -1);
  return s;
}

// Does the (unquoted) value carry a `*` wildcard?
function hasWildcard(s) {
  return s.includes("*");
}

// ── Parser ──────────────────────────────────────────────────────────
//
// AST node shapes (all plain objects, structurally cloneable):
//   { type: 'and', children: Node[] }
//   { type: 'or',  children: Node[] }
//   { type: 'not', child: Node }
//   { type: 'pred', field, key, def, op, value, wildcard, raw, span }
//   { type: 'text', value, raw, span }          // bare full-text term
//   { type: 'error', raw, span, message }        // unparseable clause, kept for display
//
// `op` ∈ eq | ne | in | contains | gt | gte | lt | lte
// `value` is a string for scalar ops, string[] for `in`. For `contains` the
// stored value keeps the user's `*` wildcards; compile translates `*`→`%`.

export function parseQuery(input) {
  const text = String(input || "");
  const tokens = tokenize(text);
  const errors = [];
  const warnings = [];
  let pos = 0;

  const peek = () => tokens[pos];
  const next = () => tokens[pos++];
  const keywordOf = (tok) =>
    tok && tok.type === "word" && KEYWORDS.has(tok.value.toLowerCase()) && !hasClauseOp(tok.value)
      ? tok.value.toLowerCase()
      : null;

  function parseOr() {
    const children = [parseAnd()];
    while (true) {
      const tok = peek();
      const kw = keywordOf(tok);
      const isBarOr = tok && tok.type === "word" && tok.value === "|";
      if (kw === "or" || isBarOr) { next(); children.push(parseAnd()); continue; }
      break;
    }
    return children.length === 1 ? children[0] : { type: "or", children };
  }

  function warnSplitPhrase(run) {
    if (run.length < 2) return;
    warnings.push({
      message: `Words are searched separately. For the exact phrase, use quotes: "${run.map((n) => n.value).join(" ")}"`,
      span: [run[0].span[0], run[run.length - 1].span[1]],
    });
  }

  function parseAnd() {
    const isBareWord = (node) => node.type === "text" && !node.raw.startsWith('"');
    const children = [parseNot()];
    let run = isBareWord(children[0]) ? [children[0]] : [];
    while (true) {
      const tok = peek();
      if (!tok || tok.type === "rparen") break;
      const kw = keywordOf(tok);
      if (kw === "or") break;            // belongs to the OR level
      if (tok.type === "word" && tok.value === "|") break;
      let explicit = false;
      if (kw === "and" || (tok.type === "word" && tok.value === "&")) { next(); explicit = true; }
      const child = parseNot();
      if (isBareWord(child) && !explicit) {
        run.push(child);
      } else {
        warnSplitPhrase(run);
        run = isBareWord(child) ? [child] : [];
      }
      children.push(child);
    }
    warnSplitPhrase(run);
    return children.length === 1 ? children[0] : { type: "and", children };
  }

  function parseNot() {
    const tok = peek();
    const kw = keywordOf(tok);
    if (kw === "not") { next(); return { type: "not", child: parseNot() }; }
    // Leading `-` on a word negates that single clause (e.g. `-endpoint:x`).
    if (tok && tok.type === "word" && tok.value.startsWith("-") && tok.value.length > 1) {
      const stripped = { ...tok, value: tok.value.slice(1) };
      tokens[pos] = stripped; // rewrite in place so parsePrimary sees the bare word
      return { type: "not", child: parsePrimary() };
    }
    return parsePrimary();
  }

  function parsePrimary() {
    const tok = peek();
    if (!tok) {
      errors.push({ message: "unexpected end of query", span: [text.length, text.length] });
      return { type: "error", raw: "", span: [text.length, text.length], message: "unexpected end of query" };
    }
    if (tok.type === "lparen") {
      next();
      const inner = parseOr();
      const close = peek();
      if (close && close.type === "rparen") next();
      else errors.push({ message: "missing closing )", span: [tok.start, text.length] });
      return inner;
    }
    if (tok.type === "rparen") {
      next();
      errors.push({ message: "unexpected )", span: [tok.start, tok.end] });
      return { type: "error", raw: ")", span: [tok.start, tok.end], message: "unexpected )" };
    }
    return parseClause(next());
  }

  function parseClause(tok) {
    const raw = tok.value;
    const span = [tok.start, tok.end];
    const parsed = splitClause(raw);
    if (!parsed) {
      // No operator → bare full-text term.
      return { type: "text", value: unquote(raw), raw, span };
    }
    const { fieldTok, opTok, valueRaw } = parsed;
    const resolved = resolveField(fieldTok);
    if (!resolved) {
      warnings.push({ message: `unknown field "${fieldTok}"`, span });
      return { type: "error", raw, span, message: `unknown field "${fieldTok}"` };
    }
    const { key, def } = resolved;
    const node = buildPred(key, def, opTok, valueRaw, raw, span);
    // Validation warnings (non-fatal — the clause still renders/compiles best-effort).
    if (!OPS_BY_KIND[def.kind]?.has(node.op)) {
      warnings.push({ message: `operator not valid for ${key} (${def.kind})`, span });
    }
    return node;
  }

  const ast = tokens.length ? parseOr() : null;
  // Trailing tokens after a complete parse (e.g. stray `)`) — surface, don't crash.
  if (pos < tokens.length) {
    const tok = tokens[pos];
    errors.push({ message: `unexpected "${tok.value ?? tok.type}"`, span: [tok.start, tok.end] });
  }
  return { ast, errors, warnings };
}

// Does this word contain a clause operator (so it's a clause, not a keyword)?
function hasClauseOp(word) {
  return splitClause(word) != null;
}

// Split a raw word into { fieldTok, opTok, valueRaw } at the FIRST operator
// that follows a valid field token. Returns null when there's no operator
// (→ bare text term). Field token must be `[A-Za-z_][A-Za-z0-9_]*`.
function splitClause(word) {
  const m = /^([A-Za-z_][A-Za-z0-9_]*)(!=|>=|<=|:|>|<)([\s\S]*)$/.exec(word);
  if (!m) return null;
  return { fieldTok: m[1], opTok: m[2], valueRaw: m[3] };
}

function buildPred(key, def, opTok, valueRaw, raw, span) {
  const base = { type: "pred", field: def.field ?? null, key, def, raw, span };
  if (opTok !== ":") {
    // Comparison / not-equal operator.
    const op = OP_NAME[opTok];
    return { ...base, op, value: unquote(valueRaw), wildcard: false };
  }
  // `:` — eq / in / contains depending on value shape.
  const quoted = valueRaw.length >= 2 && valueRaw[0] === '"';
  if (!quoted && valueRaw.includes(",")) {
    const list = valueRaw.split(",").map((v) => unquote(v.trim())).filter((v) => v.length);
    return { ...base, op: "in", value: list, wildcard: false };
  }
  const v = unquote(valueRaw);
  if (!quoted && hasWildcard(v)) return { ...base, op: "contains", value: v, wildcard: true };
  return { ...base, op: "eq", value: v, wildcard: false };
}

// ── Serializer (AST → text) ──────────────────────────────────────────
//
// Round-trips a parsed AST back to query text. Used to (a) render a
// sidebar-derived scope as editable query text and (b) normalize input.
// Not guaranteed byte-identical to the user's input (whitespace, redundant
// parens), but semantically stable: parse(serialize(ast)) ≡ ast.

export function serializeQuery(node) {
  if (!node) return "";
  switch (node.type) {
    case "or":
      return node.children.map((c) => wrapIfNeeded(c, "or")).join(" OR ");
    case "and":
      return node.children.map((c) => wrapIfNeeded(c, "and")).join(" ");
    case "not": {
      const inner = node.child;
      // `-field:value` for a single pred; `NOT (...)` for groups.
      if (inner.type === "pred" || inner.type === "text") return "-" + serializeQuery(inner);
      return "NOT " + wrapIfNeeded(inner, "not");
    }
    case "text":
      return needsQuotes(node.value) ? `"${node.value}"` : node.value;
    case "pred":
      return serializePred(node);
    case "error":
      return node.raw || "";
    default:
      return "";
  }
}

function serializePred(node) {
  const f = node.key;
  const q = (v) => (needsQuotes(v) ? `"${v}"` : v);
  switch (node.op) {
    case "eq": return `${f}:${q(node.value)}`;
    case "contains": return `${f}:${node.value}`; // value already carries `*`
    case "in": return `${f}:${node.value.map(q).join(",")}`;
    case "ne": return `${f}!=${q(node.value)}`;
    case "gt": return `${f}>${node.value}`;
    case "gte": return `${f}>=${node.value}`;
    case "lt": return `${f}<${node.value}`;
    case "lte": return `${f}<=${node.value}`;
    default: return node.raw || "";
  }
}

function needsQuotes(v) {
  return typeof v === "string" && /[\s()|]/.test(v);
}

// Parenthesize a child when its precedence is looser than the parent context.
function wrapIfNeeded(child, parentCtx) {
  if (parentCtx === "or") return serializeQuery(child); // OR is lowest; no wrap needed
  // Under NOT, wrap ANY compound — NOT binds tighter than both AND and OR, so
  // `not[and[a,b]]` must serialize as `NOT (a b)`, not `NOT a b` (= (NOT a) AND b).
  if (parentCtx === "not" && (child.type === "or" || child.type === "and")) return `(${serializeQuery(child)})`;
  // Under AND, wrap an OR group (AND binds tighter than OR).
  if (child.type === "or") return `(${serializeQuery(child)})`;
  return serializeQuery(child);
}

// ── Precedence grouping (for the CodeMirror query bar) ───────────────
//
// Pure helpers the QueryBar uses to show which clauses bind together: an AND-run
// that is an operand of an OR (AND binds tighter than OR) is rendered grouped.

// Char span of any node. Leaves carry `span`; AND/OR/NOT don't, so derive from
// descendants (min start … max end).
export function nodeSpan(node) {
  if (!node) return null;
  if (node.span) return node.span;
  if (node.type === "not") return nodeSpan(node.child);
  if (node.type === "and" || node.type === "or") {
    let lo = Infinity, hi = -Infinity;
    for (const c of node.children) {
      const s = nodeSpan(c);
      if (s) { lo = Math.min(lo, s[0]); hi = Math.max(hi, s[1]); }
    }
    return hi >= lo ? [lo, hi] : null;
  }
  return null;
}

// Char ranges [start,end) of implied-precedence groups: an AND-run that is an
// operand of an OR (AND binds tighter than OR). The QueryBar uses these to give
// those clauses a dotted-green "grouped" pill, so the precedence is visible.
export function impliedGroupRanges(ast) {
  const ranges = [];
  (function walk(node, parentType) {
    if (!node) return;
    if (node.type === "and" && parentType === "or") {
      const s = nodeSpan(node);
      if (s) ranges.push(s);
    }
    if (node.type === "and" || node.type === "or") node.children.forEach((c) => walk(c, node.type));
    else if (node.type === "not") walk(node.child, "not");
  })(ast, null);
  return ranges;
}

// Tokenize a query into pill-renderable spans for the QueryBar: { type, start, end }
// where type ∈ "clause" | "or" | "paren". Honors double-quoted spans (so a quoted
// phrase like `"a b"` or a quoted value `cluster:"auth server"` is ONE token, not
// split on the space) and matches the parser's case-insensitive OR / `|`. Note:
// explicit `AND`/`NOT` keywords render as plain clause tokens here — uncommon in
// practice (adjacency = AND, `-` = NOT); the parser still drives the real precedence.
export function tokSpans(s) {
  const toks = [];
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (c === " " || c === "\t" || c === "\n") { i++; continue; }
    if (c === "(" || c === ")") { toks.push({ type: "paren", start: i, end: i + 1 }); i++; continue; }
    const start = i;
    while (i < s.length) {
      const ch = s[i];
      if (ch === " " || ch === "\t" || ch === "\n" || ch === "(" || ch === ")") break;
      if (ch === '"') { i++; while (i < s.length && s[i] !== '"') i++; if (i < s.length) i++; continue; } // through the closing quote
      i++;
    }
    const word = s.slice(start, i);
    toks.push({ type: /^(or|\|)$/i.test(word) ? "or" : "clause", start, end: i });
  }
  return toks;
}
